import { useCallback, useMemo } from 'react';
import {
  GetNotificationsQuery,
  GetNotificationsQueryVariables,
  NotificationEvent,
  NotificationFilterInput,
  NotificationsOrderByInput,
  SortOrder,
  useGetNotificationsQuery,
} from '../../gql/generated';
import { useNotificationFilters } from '../../hooks/useNotificationFilters';
import { useTableState } from '../../hooks/useTableState';

export function useNotificationsTable() {
  const { graphQLFilter } = useNotificationFilters();

  const tableState = useTableState<
    NotificationEvent,
    NotificationFilterInput,
    GetNotificationsQuery,
    GetNotificationsQueryVariables
  >({
    useQuery: useGetNotificationsQuery,
    dataAccessor: (data) => data?.notifications?.rows ?? [],
    buildFilter: () => graphQLFilter as NotificationFilterInput | undefined,
    buildOrderBy: (sorting): NotificationsOrderByInput => {
      if (sorting.length === 0) {
        return { timeUtc: SortOrder.desc };
      }

      const firstSort = sorting[0];
      const columnToField: Record<string, keyof NotificationsOrderByInput> = {
        timeUtc: 'timeUtc',
        alarmTriggerState: 'alarmTriggerState',
        machineName: 'machineName',
        alarmName: 'alarmName',
      };

      const fieldName = columnToField[firstSort.id];
      if (!fieldName) {
        return { timeUtc: SortOrder.desc };
      }

      return {
        [fieldName]: firstSort.desc ? SortOrder.desc : SortOrder.asc,
      } as NotificationsOrderByInput;
    },
    localStoragePrefix: 'notifications',
    defaultPageSize: 20,
  });

  const totalCount = useMemo(() => {
    return tableState.rawData?.notifications?.count ?? 0;
  }, [tableState.rawData]);

  const totalPages = useMemo(() => {
    return totalCount > 0
      ? Math.ceil(totalCount / tableState.pagination.pageSize)
      : 0;
  }, [totalCount, tableState.pagination.pageSize]);

  const setPageIndex = useCallback(
    (pageIndex: number) => {
      tableState.onPaginationChange({
        pageIndex,
        pageSize: tableState.pagination.pageSize,
      });
    },
    [tableState]
  );

  const setPageSize = useCallback(
    (pageSize: number) => {
      tableState.onPaginationChange({ pageIndex: 0, pageSize });
    },
    [tableState]
  );

  return {
    ...tableState,
    notifications: tableState.data,
    refetch: tableState.refetch,
    summary: tableState.rawData?.notifications?.summary,
    totalCount,
    totalPages,

    pageIndex: tableState.pagination.pageIndex,
    pageSize: tableState.pagination.pageSize,
    setPageIndex,
    setPageSize,

    setSorting: tableState.onSortingChange,
    setColumnPinning: tableState.onColumnPinningChange,
    setColumnVisibility: tableState.onColumnVisibilityChange,
    setColumnOrder: tableState.onColumnOrderChange,
  };
}
